import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DropdownButton, { BlazeDropdownItem } from 'components/ui/dropdown/DropdownButton/DropdownButton';
import { BRAND_PRIMARY_RGB, addBrandLogo } from 'views/Admin/pdf/pdfBranding';
import { PeriodSelection, UsageFilters, UsageReportRow } from './types';

interface Props {
  rows: UsageReportRow[];
  filters: UsageFilters;
  workspaceNamesById: Map<number, string>;
  onError: (error: unknown) => void;
  fetchAllRows: () => Promise<UsageReportRow[]>;
}

const periodLabel = (period: PeriodSelection): string => {
  if (period.kind === 'preset') return period.value.toLowerCase().replace(/_/g, '-');
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return `custom-${fmt(period.from)}_${fmt(period.to)}`;
};

const buildFilename = (period: PeriodSelection, ext: 'xlsx' | 'pdf'): string => {
  const today = new Date().toISOString().slice(0, 10);
  return `usage-report_${periodLabel(period)}_${today}.${ext}`;
};

// Fields are typed as string but the backend may send null/empty — show "N/A" when there
// is no data so the export never renders a literal "null".
const orNa = (value: string | null | undefined): string => (value == null || value.trim() === '' ? 'N/A' : value);

const formatRowForExport = (row: UsageReportRow) => ({
  'Service name': orNa(row.serviceName),
  Workspace: orNa(row.workspaceName),
  Location: orNa(row.locationName),
  'Created by': orNa(row.createdBy),
  'Deployed at': orNa(row.deployedAt),
  'Last used': orNa(row.lastUsedAt),
  Hits: row.hits,
  Misses: row.misses,
  Total: row.total,
  Status: row.status === 'ACTIVE' ? 'Active' : row.status === 'IDLE' ? 'Idle' : row.status === 'DELETED' ? 'Deleted' : 'Never used',
});

const buildFilterSummary = (filters: UsageFilters, workspaceNamesById: Map<number, string>): string => {
  const workspacePart =
    filters.workspaceIds.length === 0
      ? 'All workspaces'
      : filters.workspaceIds.map(id => workspaceNamesById.get(id) ?? String(id)).join(', ');
  const deletedPart = filters.includeDeletedMocks ? 'Yes' : 'No';
  return `Workspaces: ${workspacePart} | Include deleted: ${deletedPart}`;
};

const UsageReportExportButtons: React.FC<Props> = ({ rows, filters, workspaceNamesById, onError, fetchAllRows }) => {
  const { t } = useTranslation('admin');
  const period: PeriodSelection = filters.period;
  const disabled = rows.length === 0;
  const [isExporting, setIsExporting] = useState(false);

  const handleXlsx = async () => {
    setIsExporting(true);
    try {
      const allRows = await fetchAllRows();
      const data = allRows.map(formatRowForExport);
      const sheet = XLSX.utils.json_to_sheet(data);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Usage Report');
      XLSX.writeFile(book, buildFilename(period, 'xlsx'));
    } catch (error) {
      onError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePdf = async () => {
    setIsExporting(true);
    try {
      const allRows = await fetchAllRows();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      addBrandLogo(doc);
      doc.setFontSize(14);
      doc.text('Usage Report', 40, 30);
      doc.setFontSize(10);
      doc.text(`Period: ${periodLabel(period)} — Generated: ${new Date().toISOString()}`, 40, 46);
      doc.text(`Filters: ${buildFilterSummary(filters, workspaceNamesById)}`, 40, 58);

      const head = [
        [
          'Service name',
          'Workspace',
          'Location',
          'Created by',
          'Deployed at',
          'Last used',
          'Hits',
          'Misses',
          'Total',
          'Status',
        ],
      ];
      const body = allRows.map(r => Object.values(formatRowForExport(r)).map(String));

      autoTable(doc, {
        head,
        body,
        startY: 72,
        styles: { fontSize: 8 },
        headStyles: { fillColor: BRAND_PRIMARY_RGB },
        theme: 'striped',
      });
      doc.save(buildFilename(period, 'pdf'));
    } catch (error) {
      onError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const dropdownItems: BlazeDropdownItem[] = [
    { id: 'xlsx', content: t('usageReport.export.xlsx'), fnCallback: handleXlsx },
    { id: 'pdf', content: t('usageReport.export.pdf'), fnCallback: handlePdf },
  ];

  return (
    <div className="usage-report-download">
      <DropdownButton
        disabled={disabled || isExporting}
        toggleText={t('usageReport.export.download')}
        toggleTooltip={t('usageReport.export.download')}
        dropdownItems={dropdownItems}
        dropdownToggleClassName="btn btn-primary"
        dropDownPlacement="RIGHT"
      />
    </div>
  );
};

export default UsageReportExportButtons;
